import { focusable } from 'tabbable';

export function findNodeByContent (text, root = document.body) {
    const treeWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

    const nodeList = [];

    while (treeWalker.nextNode()) {
        const node = treeWalker.currentNode;

        if (node.nodeType === Node.TEXT_NODE && node.textContent.includes(text)) {
            nodeList.push(node.parentNode);
        }
    }

    return nodeList;
}

function elementHasOverflowY ({ clientHeight, scrollHeight }) {
    return scrollHeight > clientHeight;
}

export function elementHasOverflowX ({ clientWidth, scrollWidth }) {
    return scrollWidth > clientWidth;
}

export function elementHasOverflow (element) {
    return elementHasOverflowY(element) || elementHasOverflowX(element);
}

// focuses the first focusable element in a node
export function getFirstFocusableNode (container) {
    if (typeof container?.querySelectorAll !== 'function') return undefined;

    return focusable(container)[0];
}

export function getLastFocusableNode (container) {
    if (typeof container?.querySelectorAll !== 'function') return undefined;

    return focusable(container).pop();
}

/**
 * Checks if an element receiving focus/click is within a containing element.
 * Handles both focus events (blur/focusout) and mouse events (mousedown/click).
 *
 * @param {HTMLElement} container - The containing DOM element (must be a valid DOM node with contains method)
 * @param {HTMLElement|null|undefined} receivingElement - The element that is receiving focus/click (e.g., event.relatedTarget, event.target)
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeSelf - If true, considers the container itself as "within" (default: true)
 * @returns {boolean} - True if landing element is within container, false otherwise
 * @throws {TypeError} - Throws if container is not a valid DOM element with a contains method
 */
export function isElementWithinContainer (container, receivingElement, options = {}) {
    const {
        includeSelf = true,
    } = options;

    // Need to provide a valid container element
    if (!container || typeof container.contains !== 'function') {
        throw new TypeError('isElementWithinContainer: container must be a valid DOM element with a contains method');
    }

    // If null/undefined, it means focus/click went outside the document
    if (!receivingElement) {
        return false;
    }

    if (receivingElement === container) {
        return includeSelf;
    }

    return container.contains(receivingElement);
}

export function getScrollParent (element) {
    if (element == null) {
        return null;
    }

    if (elementHasOverflowY(element)) {
        return element;
    }

    return getScrollParent(element.parentNode);
}
